import { supabase } from './client';
import { normalizeProfile } from './utils';

const isTransientNetworkError = (error: unknown): boolean => {
  const message =
    typeof error === 'string'
      ? error
      : (error as { message?: string } | null | undefined)?.message || '';

  const normalized = message.toLowerCase();
  return (
    normalized.includes('load failed') ||
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('network request failed')
  );
};

export interface UserProfile {
  id: string;
  username: string;
  avatar_url: string | null;
  city_id?: string | null;
  name?: string | null;
  surname?: string | null;
  display_name?: string | null;
  transport_type?: string | null;
  appearance_settings?: {
    customPrimary?: string | null;
    [key: string]: any;
  } | null;
  email?: string | null;
  karma_points: number;
  relevant_location_display_name: string | null;
  relevant_latitude: number | null;
  relevant_longitude: number | null;
  bank_accounts?: any[];
  bank_cbu?: string | null;
  bank_name?: string | null;
  bank_account_holder?: string | null;
  phone_number?: string | null;
  verification_photos?: {
    dniAnh: string | null;
    dniRev: string | null;
    withDni: string | null;
  };
  verification_status?: 'pending' | 'submitted' | 'verified' | 'rejected' | 'expired';
  verified_at?: string | null;
}

const PROFILE_SELECT_WITH_SEPARATED_NAMES = 'id,username,avatar_url,city_id,name,surname,display_name,transport_type,email,karma_points,relevant_location_display_name,relevant_latitude,relevant_longitude,bank_accounts,bank_cbu,bank_name,bank_account_holder,phone_number,appearance_settings,verification_photos,verification_status,verified_at';
const PROFILE_SELECT_LEGACY = 'id,username,avatar_url,city_id,display_name,transport_type,email,karma_points,relevant_location_display_name,relevant_latitude,relevant_longitude,bank_accounts,bank_cbu,bank_name,bank_account_holder,phone_number,appearance_settings,verification_photos,verification_status,verified_at';

export const getProfileById = async (userId: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(PROFILE_SELECT_WITH_SEPARATED_NAMES)
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      // Fallback for environments where name/surname columns are not yet available.
      const { data: legacyData, error: legacyError } = await supabase
        .from('profiles')
        .select(PROFILE_SELECT_LEGACY)
        .eq('id', userId)
        .maybeSingle();

      if (!legacyError) {
        return (legacyData as UserProfile | null) || null;
      }

      if (isTransientNetworkError(error)) {
        console.warn('Profile fetch skipped due to transient network error:', { userId, message: (error as any)?.message });
      } else {
        console.error('Error fetching profile by ID:', error);
      }
      return null;
    }

    return data || null;
  } catch (err) {
    if (isTransientNetworkError(err)) {
      console.warn('Profile fetch failed due to transient network error:', { userId, message: (err as any)?.message });
      return null;
    }
    console.error('Unexpected error fetching profile by ID:', err);
    return null;
  }
};

export const getProfileByUsername = async (username: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(PROFILE_SELECT_WITH_SEPARATED_NAMES)
      .eq('username', username)
      .maybeSingle();

    if (error) {
      const { data: legacyData, error: legacyError } = await supabase
        .from('profiles')
        .select(PROFILE_SELECT_LEGACY)
        .eq('username', username)
        .maybeSingle();

      if (!legacyError) {
        return (legacyData as UserProfile | null) || null;
      }

      return null;
    }

    return data || null;
  } catch {
    return null;
  }
};

export const getProfileByEmail = async (email: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(PROFILE_SELECT_WITH_SEPARATED_NAMES)
      .eq('email', email)
      .single();

    if (error) {
      const { data: legacyData, error: legacyError } = await supabase
        .from('profiles')
        .select(PROFILE_SELECT_LEGACY)
        .eq('email', email)
        .single();

      if (!legacyError) {
        return legacyData as UserProfile;
      }

      console.warn('getProfileByEmail warning:', error.message || error);
      return null;
    }
    return data as UserProfile;
  } catch (err) {
    console.warn('getProfileByEmail failed', err);
    return null;
  }
};

export const getUsersForDiscovery = async (currentUserId: string, searchTerm: string | null) => {
  let query = supabase
    .from('profiles')
    .select('id,username,avatar_url')
    .neq('id', currentUserId);

  if (searchTerm) {
    query = query.ilike('username', `%${searchTerm}%`);
  }

  // Limit to 50 results to reduce egress (can return 100k+ users without limit)
  // Prevents 10-50MB per query when searchTerm is null
  query = query.limit(50);

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching users for discovery:', error);
    throw error;
  }
  return data;
};

export const getProfilesByIds = async (userIds: string[]): Promise<UserProfile[]> => {
  if (userIds.length === 0) {
    return [];
  }
  
  const { data, error } = await supabase
    .from('profiles')
    .select('id,username,avatar_url,display_name,appearance_settings,karma_points,relevant_location_display_name,relevant_latitude,relevant_longitude,verification_status')
    .in('id', userIds);

  if (error) {
    console.error('Error fetching profiles by IDs:', error);
    throw error;
  }

  const profilesMap = new Map<string, UserProfile>();
  (data as UserProfile[]).forEach(profile => profilesMap.set(profile.id, profile));
  // Ensure the order is preserved and filter out any nulls
  return userIds.map(id => profilesMap.get(id)).filter((p): p is UserProfile => !!p);
};

export const getUsersByFollowerCount = async () => {
  const { data, error } = await supabase.rpc('get_users_by_follower_count');

  if (error) {
    console.error('Error fetching users by follower count:', error);
    throw error;
  }
  return data.map((item: any) => ({
    ...item,
    profiles: normalizeProfile(item.profiles),
  }));
};

export const updateProfileAvatarUrl = async (userId: string, avatarUrl: string | null) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ avatar_url: avatarUrl })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating profile avatar URL:', error);
    throw error;
  }
  return data;
};

export const updateProfileUsername = async (userId: string, newUsername: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ username: newUsername })
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating profile username:', error);
    throw error;
  }
  return data;
};

export const updateProfileDisplayName = async (userId: string, newDisplayName: string | null) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ display_name: newDisplayName })
      .eq('id', userId)
      .select('id,display_name')
      .single();

    if (error) {
      console.error('Error updating profile display name:', error);
      throw error;
    }
    return data;
  } catch (err) {
    console.error('Unexpected error updating display name:', err);
    throw err;
  }
};

// NEW: Function to update user's relevant location
export const updateProfileRelevantLocation = async (
  userId: string,
  locationDisplayName: string | null,
  latitude: number | null,
  longitude: number | null
) => {

  const { data, error } = await supabase
    .from('profiles')
    .update({
      relevant_location_display_name: locationDisplayName,
      relevant_latitude: latitude,
      relevant_longitude: longitude,
    })
    .eq('id', userId)
    .select('id,relevant_location_display_name,relevant_latitude,relevant_longitude')
    .single();

  if (error) {
    console.error('Error updating profile relevant location:', error);
    // surface error details for debugging
    throw error;
  }

  // Log returned row for easier debugging in browser console
   
  return data;
};

// Appearance settings persistence (JSONB) stored on profiles.appearance_settings
export const getProfileAppearance = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('appearance_settings')
      .eq('id', userId)
      .single();

    if (error) {
      // If the column does not exist, Supabase will return an error; handle gracefully.
      console.warn('getProfileAppearance warning:', error.message || error);
      return null;
    }

    return data?.appearance_settings ?? null;
  } catch (err) {
    console.error('Unexpected error getting profile appearance:', err);
    return null;
  }
};

export const updateProfileAppearance = async (userId: string, appearance: any) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ appearance_settings: appearance })
      .eq('id', userId)
      .select('id,appearance_settings')
      .single();

    if (error) {
      console.warn('updateProfileAppearance warning:', error.message || error);
      throw error;
    }
    // Return the stored appearance_settings for verification
    return data?.appearance_settings ?? null;
  } catch (err) {
    console.error('Unexpected error updating profile appearance:', err);
    throw err;
  }
};

// NEW: Get email for a user (for store operations like invoicing, marketing)
export const getUserEmail = async (userId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', userId)
      .single();

    if (error) {
      console.warn('getUserEmail warning:', error.message || error);
      return null;
    }
    return data?.email ?? null;
  } catch (err) {
    console.error('getUserEmail failed:', err);
    return null;
  }
};

// NEW: Get emails for multiple users (batch operation)
export const getUsersEmails = async (userIds: string[]): Promise<Map<string, string | null>> => {
  if (!userIds.length) return new Map();

  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id,email')
      .in('id', userIds);

    if (error) {
      console.warn('getUsersEmails warning:', error.message || error);
      return new Map();
    }

    const emailMap = new Map<string, string | null>();
    data?.forEach((row: any) => {
      emailMap.set(row.id, row.email ?? null);
    });
    return emailMap;
  } catch (err) {
    console.error('getUsersEmails failed:', err);
    return new Map();
  }
};

// NEW: Get all store members' emails (for store operations)
export const getStoremembersEmails = async (storeId: string): Promise<Map<string, string | null>> => {
  try {
    const { data, error } = await supabase
      .from('store_members')
      .select('user_id')
      .eq('store_id', storeId);

    if (error) {
      console.warn('getStoremembersEmails - failed to fetch members:', error.message || error);
      return new Map();
    }

    const userIds = data?.map((m: any) => m.user_id) ?? [];
    return getUsersEmails(userIds);
  } catch (err) {
    console.error('getStoremembersEmails failed:', err);
    return new Map();
  }
};

/**
 * Elimina permanentemente la cuenta del usuario autenticado
 * Esta función llama a un RPC que elimina el usuario de auth.users
 * y todos los datos relacionados
 */
export async function deleteUserAccount(): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.rpc('delete_user_account');

    if (error) {
      console.error('Error deleting user account:', error);
      return {
        success: false,
        error: error.message || 'Error desconocido al eliminar la cuenta'
      };
    }

    // El RPC devuelve un objeto JSON con success y message/error
    if (data && typeof data === 'object') {
      if (data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: data.error || 'Error al eliminar la cuenta'
        };
      }
    }

    return { success: true };
  } catch (err) {
    console.error('deleteUserAccount failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Error inesperado'
    };
  }
}